import * as React from 'react';
import {
  TooltipTrigger as RACTooltipTrigger,
  Tooltip as RACTooltip,
  Focusable,
  type TooltipProps as RACTooltipProps,
} from 'react-aria-components';

import { cn } from '../lib/cn';

/**
 * Klaret Tooltip (handoff §10.8).
 * Primitive: RAC TooltipTrigger + Tooltip.
 * Inverted: --tvl-ink fill + paper text (light) / --tvl-overlay fill + paper text (dark).
 * caption size, 6/8px padding, --tvl-r1, --tvl-e1, max-width 240px.
 * 400ms delay. Never interactive.
 * All compound names preserved: TooltipProvider, Tooltip, TooltipTrigger, TooltipContent.
 */

/** Provider shim — RAC has no separate provider; kept for API compat. */
export const TooltipProvider: React.FC<{
  children?: React.ReactNode;
  delayDuration?: number;
}> = ({ children }) => <>{children}</>;

/**
 * Trigger element. RAC needs the trigger to be focusable, so we wrap the child
 * in `Focusable` (works for non-interactive content like badges). `asChild` is
 * accepted for back-compat with the old Radix API and ignored.
 */
export const TooltipTrigger = ({
  children,
}: {
  asChild?: boolean;
  className?: string;
  children?: React.ReactNode;
}) => <Focusable>{children as never}</Focusable>;

/**
 * Root Tooltip — maps to RAC TooltipTrigger so that:
 *   <Tooltip><TooltipTrigger>…</TooltipTrigger><TooltipContent>…</TooltipContent></Tooltip>
 * works identically to the old Radix API.
 */
export const Tooltip = RACTooltipTrigger;

export interface TooltipContentProps extends Omit<RACTooltipProps, 'children' | 'className'> {
  className?: string;
  children?: React.ReactNode;
  sideOffset?: number;
}

export const TooltipContent = React.forwardRef<HTMLDivElement, TooltipContentProps>(
  ({ className, sideOffset = 6, children, ...props }, _ref) => (
    <RACTooltip
      offset={sideOffset}
      className={cn(
        'z-[var(--tvl-z-tooltip)] max-w-[240px] outline-none',
        /* inverted: ink fill, paper text in light; overlay fill, paper text in dark */
        'bg-[var(--tvl-ink)] text-[var(--tvl-raised)]',
        '[data-theme=dark]_&:bg-[var(--tvl-overlay)] [data-theme=dark]_&:text-[var(--tvl-ink)]',
        'rounded-[var(--tvl-r1)] shadow-[var(--tvl-e1)]',
        'px-2 py-1.5 text-xs',
        'entering:animate-[tooltip-enter_var(--tvl-d-fast)_var(--tvl-ease-out)_forwards]',
        'exiting:animate-[tooltip-exit_var(--tvl-d-fast)_var(--tvl-ease-out)_forwards]',
        className,
      )}
      {...props}
    >
      {children}
    </RACTooltip>
  ),
);
TooltipContent.displayName = 'TooltipContent';
